import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { StepValidationResult } from 'features/libra/workflow/workflowStepsApi'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import { GeckoComponent, GeckoInspection, RolodexEntryType } from 'types'
import {
  FfsAnnex4bBinningConfigurationData,
  measurementFromConfigJson,
} from 'types/entries/geckoFfsAnnex4bBinningConfiguration'

import { resolveFfsArtifactIds } from './ffsIds'
import { useActiveLtaSet } from './useFfsIndicationSets'

interface UseFfsBinningValidationArgs {
  component: GeckoComponent | undefined
  componentId: string | undefined
  inspectionId: string | undefined
  unitId: string | undefined
  /** The inspection entry (already loaded by `useFfsTicketContext`). */
  inspection: GeckoInspection | undefined
}

interface UseFfsBinningValidationResult {
  validation: StepValidationResult
  hasFfsConfig: boolean
  hasBinSizes: boolean
  hasThreshold: boolean
  hasLtaSet: boolean
  /** The parsed FFS configuration entry (so the step can reuse it for prefill). */
  ffsConfig: FfsAnnex4bBinningConfigurationData | undefined
  isLoading: boolean
  /** Refreshes BOTH the FFS config entry AND the indication-set discovery query. */
  forceRefresh: () => Promise<unknown>
}

/**
 * Validates the binning step's prerequisites:
 *  1. The ticket-scoped `ffs-configuration_{ticketId}_{componentId}` entry
 *     exists with required fields populated (legacy unscoped id queried
 *     as a fallback when no `ComponentScopeContext` is mounted; see
 *     `ffsIds.ts`)
 *  2. The inspection has `x_bin_size` / `y_bin_size` set
 *  3. An LTA indication set exists for this (component, inspection), tagged to
 *     the inspection (no untagged / wrong-inspection legacy sets).
 *
 * Owns its own indication-set query (rather than going through
 * `useIndicationSets`) so that `forceRefresh` can invalidate both the FFS
 * config AND the set discovery -- the previous indirection meant generating
 * LTAs would leave this step blocked on the stale "no set" result until
 * remount.
 */
export function useFfsBinningValidation({
  component,
  componentId,
  inspectionId,
  unitId,
  inspection,
}: UseFfsBinningValidationArgs): UseFfsBinningValidationResult {
  const { ticketId } = useTicketAccess()
  const scope = useComponentScope()
  // Asset (tabbed) tickets use the ticket-scoped FFS config id; legacy
  // single-component tickets without a `ComponentScopeContext` still write
  // the unscoped `ffs-configuration_{componentId}` id. Query whichever
  // applies — and document a TODO: legacy entries stay orphaned once the
  // user resaves under the ticket-scoped id (acceptable until those
  // tickets are migrated).
  const ffsConfigIds = useMemo<string[]>(() => {
    if (!componentId) return []
    const { ffsConfigId } = resolveFfsArtifactIds({
      scoped: Boolean(scope),
      ticketId,
      componentId,
      unit: '',
      inspection: '',
    })
    return [ffsConfigId]
  }, [componentId, ticketId, scope])

  const {
    entries: ffsConfigEntries,
    isLoading: ffsConfigLoading,
    forceRefresh: refreshFfsConfig,
  } = useRolodexEntriesWithSyncedRefreshes(
    ffsConfigIds.length > 0
      ? { type: RolodexEntryType.FFS_CONFIGURATION, ids: ffsConfigIds }
      : undefined
  )

  // Resolve the active LTA set via the shared helper (same one
  // `useFfsTriageData` uses). One id-resolution policy across all three
  // FFS paths — generation/validation/triage can't drift.
  const {
    ltaSet,
    isLoading: setsLoading,
    forceRefresh: refreshSets,
  } = useActiveLtaSet({ component, inspectionId })

  const hasLtaSet = ltaSet !== undefined

  const forceRefresh = useCallback(async () => {
    await Promise.all([refreshFfsConfig(), refreshSets()])
  }, [refreshFfsConfig, refreshSets])

  return useMemo(() => {
    const ffsConfig = ffsConfigEntries[0]?.data as
      | FfsAnnex4bBinningConfigurationData
      | undefined
    // Bin-size config: t_rd + FCA are needed to compute and save L_a.
    // LTA threshold is NOT required here -- it gates LTA generation, not
    // saving bin sizes -- so it has its own `hasThreshold` field.
    const fallbackUnitId = ffsConfig?.thicknessUnitId
    const uniformThickness = measurementFromConfigJson(
      ffsConfig?.uniformThickness,
      fallbackUnitId
    )
    const futureCorrosionAllowance = measurementFromConfigJson(
      ffsConfig?.futureCorrosionAllowance,
      fallbackUnitId
    )
    const localThinAreaThreshold = measurementFromConfigJson(
      ffsConfig?.localThinAreaThreshold,
      fallbackUnitId
    )
    const hasFfsConfig = Boolean(uniformThickness && futureCorrosionAllowance)
    const hasThreshold = Boolean(
      localThinAreaThreshold && localThinAreaThreshold.num > 0
    )
    const inspectionData = inspection?.data as
      | { x_bin_size?: number; y_bin_size?: number }
      | undefined
    const hasBinSizes = Boolean(
      inspectionData &&
      inspectionData.x_bin_size !== undefined &&
      inspectionData.y_bin_size !== undefined
    )

    let validation: StepValidationResult = undefined
    if (!componentId || !inspectionId || !unitId) {
      validation = {
        status: 'Error',
        message: 'Ticket is missing required component/inspection/unit tags.',
      }
    } else if (!hasFfsConfig) {
      validation = {
        status: 'Error',
        message: 'Enter t_rd and FCA, then save the bin sizes.',
      }
    } else if (!hasBinSizes) {
      validation = {
        status: 'Error',
        message: 'Save L_a bin sizes to the inspection before continuing.',
      }
    } else if (!hasThreshold) {
      validation = {
        status: 'Error',
        message: 'Enter an LTA threshold > 0.',
      }
    } else if (!hasLtaSet) {
      validation = {
        status: 'Error',
        message: 'Generate Local Thin Areas before continuing.',
      }
    }

    return {
      validation,
      hasFfsConfig,
      hasBinSizes,
      hasThreshold,
      hasLtaSet,
      ffsConfig,
      isLoading: ffsConfigLoading || setsLoading,
      forceRefresh,
    }
  }, [
    componentId,
    ffsConfigEntries,
    ffsConfigLoading,
    forceRefresh,
    hasLtaSet,
    inspection,
    inspectionId,
    setsLoading,
    unitId,
  ])
}
